import { useState, type FormEvent, type ReactNode } from 'react'
import {
  Alert,
  Box,
  Button,
  Paper,
  Stack,
  TextField,
  Typography,
} from '@mui/material'
import { useTheme } from '@mui/material/styles'
import { useTranslation } from 'react-i18next'
import config from '../config'
import { AuthThemeModeButton } from './ThemeModeButton'
import { useThemeMode } from '../contexts/ThemeModeContext'

const SITE_ACCESS_KEY = 'approval_system_site_access'
const SITE_ACCESS_USERNAME = '1'
const SITE_ACCESS_PASSWORD = '1'

type SiteAccessGateProps = {
  children: ReactNode
}

function getStoredSiteAccess(): boolean {
  return sessionStorage.getItem(SITE_ACCESS_KEY) === 'granted'
}

export default function SiteAccessGate({ children }: Readonly<SiteAccessGateProps>) {
  const { t } = useTranslation()
  const { mode } = useThemeMode()
  const theme = useTheme()
  const isDark = mode === 'dark'
  const [hasAccess, setHasAccess] = useState(getStoredSiteAccess)
  const [username, setUsername] = useState('')
  const [password, setPassword] = useState('')
  const [showError, setShowError] = useState(false)

  function handleSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault()

    if (username === SITE_ACCESS_USERNAME && password === SITE_ACCESS_PASSWORD) {
      sessionStorage.setItem(SITE_ACCESS_KEY, 'granted')
      setHasAccess(true)
      setShowError(false)
      return
    }

    setShowError(true)
  }

  if (hasAccess) return <>{children}</>

  return (
    <Box
      sx={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        background: `linear-gradient(160deg, ${theme.palette.authGradient.start} 0%, ${theme.palette.authGradient.end} 100%)`,
        p: 2,
      }}
    >
      <Paper
        component="form"
        onSubmit={handleSubmit}
        elevation={isDark ? 12 : 4}
        sx={{
          width: '100%',
          maxWidth: 420,
          borderRadius: 3,
          px: 4,
          py: 4,
          ...(isDark && { border: '1px solid rgba(255,255,255,0.1)' }),
        }}
      >
        <Stack spacing={3}>
          <Box>
            <Typography variant="overline" sx={{ color: 'primary.main', fontWeight: 800, letterSpacing: '0.18em' }}>
              {config.appName}
            </Typography>
            <Typography variant="h4" sx={{ mt: 1, fontWeight: 800 }}>
              {t('siteAccessTitle')}
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 1.25 }}>
              {t('siteAccessDescription')}
            </Typography>
          </Box>

          {showError ? <Alert severity="error">{t('siteAccessError')}</Alert> : null}

          <TextField
            label={t('siteAccessUsername')}
            value={username}
            onChange={(event) => {
              setUsername(event.target.value)
              if (showError) setShowError(false)
            }}
            autoComplete="username"
            fullWidth
          />

          <TextField
            label={t('siteAccessPassword')}
            type="password"
            value={password}
            onChange={(event) => {
              setPassword(event.target.value)
              if (showError) setShowError(false)
            }}
            autoComplete="current-password"
            fullWidth
          />

          <Button type="submit" variant="contained" size="large" fullWidth sx={{ minHeight: 48, fontWeight: 700 }}>
            {t('siteAccessSubmit')}
          </Button>
        </Stack>
      </Paper>

      <AuthThemeModeButton />
    </Box>
  )
}
